import { ErrorBoundary } from '@sentry/react'
import PropTypes from 'prop-types'
import { PureComponent, Suspense, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Outlet, ScrollRestoration, useNavigate } from 'react-router-dom'

import APP, { AuthUserSchema, Feature, print } from 'app'
import { actions, NOTIFICATIONS_STATUS, NotificationsZone } from 'notifications'
import { Link, ProgressBar, SimpleButton } from 'ui'
import { withMatch } from 'utils'

import { authAction, authFetch, authLoginByCredentials, authLogout } from 'actions/AuthActions'
import { environmentOnlineSet, environmentSet, environmentVisibilitySet } from 'actions/EnvironmentActions'
import {
  SYSTEM_MESSAGE_ACTION,
  SYSTEM_MESSAGE_INITIAL_STATE,
  systemMessageCanceled,
  systemMessageShow,
} from 'actions/SystemMessageActions'

import Header from './Header'
import OnLineStatus from './OnLineStatus'
import UserMenu from './UserMenu'

import '../../side-effects'

import './Site.css'

const PRINT_KEYS = [71, 80, 83]

const handleSaveKeyPress = (e) => {
  if ((window.navigator.platform.match('Mac') ? e.metaKey : e.ctrlKey) && PRINT_KEYS.includes(e.keyCode)) {
    e.preventDefault()
    print()
  }
}

const FixHistory = () => {
  const navigate = useNavigate()
  const historyRef = useRef({
    blocked: false,
    goBack: () => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('history object is deprecated, use useNavigate instead')
      }
      return navigate(-1)
    },
    goForward: () => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('history object is deprecated, use useNavigate instead')
      }
      return navigate(1)
    },
    push: (...args) => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('history object is deprecated, use useNavigate instead')
      }
      return navigate(...args)
    },
    replace: (url, options) => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('history object is deprecated, use useNavigate instead')
      }
      return navigate(url, {
        ...options,
        replace: true,
      })
    },
  })

  useEffect(() => {
    if (!historyRef.current.blocked) {
      Object.assign(historyRef.current, {
        goBack: () => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('history object is deprecated, use useNavigate instead')
          }
          return navigate(-1)
        },
        push: (...args) => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('history object is deprecated, use useNavigate instead')
          }
          return navigate(...args)
        },
        replace: (url, options) => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('history object is deprecated, use useNavigate instead')
          }
          return navigate(url, {
            ...options,
            replace: true,
          })
        },
      })
    }
  }, [navigate])

  APP.history = historyRef.current

  return null
}

class Site extends PureComponent {
  constructor(props) {
    super(props)
    this.props.environmentSet()
  }

  componentDidMount() {
    document.addEventListener('keydown', handleSaveKeyPress)

    window.addEventListener('resize', this.props.environmentSet)
    window.addEventListener('load', this.props.environmentSet)
    window.addEventListener('visibilitychange', this.visibilityChange)
    window.addEventListener('online', this.onlineStateChange)
    window.addEventListener('offline', this.onlineStateChange)

    // if (window.matchMedia && Feature.isEnabled('dark_mode')) {
    //   if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //     this.toggleDarkMode(true)
    //   }
    //
    //   if (window.matchMedia('(prefers-color-scheme: dark)').addEventListener) {
    //     window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.onDarkColorSchemeChange)
    //   }
    // }

    APP.notificationsAdd = this.props.addNotification

    if (APP.msg && APP.msg.logout) {
      APP.msg.logout = false
      this.props.authLogout()
    } else if (!this.props.auth.loaded && !this.props.auth.loading) {
      this.props.authFetch()
    }

    if (APP.msg && !APP.msg.token) {
      this.props.systemMessageShow({
        ...SYSTEM_MESSAGE_INITIAL_STATE,
        msg: APP.msg.content,
        options: [
          {
            ...SYSTEM_MESSAGE_ACTION,
            content: 'OK',
            onClick: () => {
              const login = APP.msg.token !== undefined
              const accessAuth = APP.msg.accessAuth || null
              const accessCode = APP.msg.accessCode || null

              APP.msg = null
              APP.accessAuth = null
              APP.accessCode = null

              this.props.systemMessageCanceled()
              APP.history.push('/')

              if (accessAuth && accessCode) {
                this.props.authLoginByCredentials(accessAuth, accessCode)
              } else if (login) {
                this.props.authAction('login')
              }
            },
            primary: true,
          },
        ],
        title: APP.msg.title,
      })
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      window.scrollTo(0, 0)
    }
    if (this.props.auth.usr !== prevProps.auth.usr) {
      Feature.setUserFunction(() => this.props.auth.usr)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.onlineStateChange)
    window.removeEventListener('offline', this.onlineStateChange)
    window.removeEventListener('visibilitychange', this.visibilityChange)
    window.removeEventListener('load', this.props.environmentSet)
    window.removeEventListener('resize', this.props.environmentSet)

    document.removeEventListener('keydown', handleSaveKeyPress)
    if (window.matchMedia && Feature.isEnabled('dark_mode')) {
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this.onDarkColorSchemeChange)
    }
  }

  callToggleLeftMenu = () => {
    if (this.toggleLeftMenu) {
      this.toggleLeftMenu?.()
    } else {
      document.location = '/'
    }
  }

  onDarkColorSchemeChange = ({ matches }) => this.toggleDarkMode(matches)

  onlineStateChange = () => this.props.environmentOnlineSet(!!window.navigator.onLine)

  setToggleLeftMenu = (toggleLeftMenu) => {
    this.toggleLeftMenu = toggleLeftMenu
  }

  toggleDarkMode = (enable) => {
    if (Feature.isEnabled('dark_mode')) {
      if (enable === false || (enable !== true && document.body.getAttribute('data-dark-mode'))) {
        document.body.removeAttribute('data-dark-mode')
        this.props.addNotification({
          id: 'data-dark-mode',
          status: NOTIFICATIONS_STATUS.INFO,
          title: 'Alto Contraste desactivado',
        })
      } else {
        document.body.setAttribute('data-dark-mode', 'true')
        this.props.addNotification({
          id: 'data-dark-mode',
          status: NOTIFICATIONS_STATUS.SUCCESS,
          title: 'Alto Contraste activado',
        })
      }
    }
  }

  toggleLeftMenu = null

  visibilityChange = () => this.props.environmentVisibilitySet(window.document.visibilityState)

  renderContent() {
    return (
      <ErrorBoundary fallback={this.renderError}>
        <Suspense fallback={<ProgressBar ariaLabel="Cargando..." value={100} />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    )
  }

  renderError({ componentStack, error, eventId }) {
    const errorReport = `
Hola SweatBits,

Ha ocurrido un error en la aplicación.

Error ID: ${eventId || 'N/A'}
URL: ${window.location.href}
Error: ${error.toString()}
    `
    return (
      <div className="flex flex-col gap-2 p-2 rounded border border-red bg-white m-1">
        <div className="text-lg font-bold border-b pb-1 border-red">Disculpa el inconveniente</div>
        <div>Ha ocurrido un error al procesar la petición</div>
        <div />
        <div>
          La descripción técnica del error es: <code className="border rounded py-1 px-2">{error.toString()}</code>
        </div>
        <div>
          Puedes recargar esta página para intentar nuevamente haciendo clic acá:{' '}
          <Link className="!text-green" href={window.location.href} onClick={() => window.location.reload()}>
            Recargar
          </Link>
        </div>
        <div>
          Puedes comunicarte con nosotros para reportar este error:{' '}
          <Link
            className="text-green"
            href={`mailto:info@sweatbits.co?subject=Reporte de error&body=${encodeURIComponent(errorReport)}`}
            target="_blank"
          >
            info@sweatbits.co
          </Link>
        </div>
        {process.env.NODE_ENV === 'development' && (
          <pre className="border rounded py-1 px-2">{componentStack.replace(/[ \t]+/gi, ' ')}</pre>
        )}
      </div>
    )
  }

  render() {
    const { usr } = this.props.auth
    return (
      <div className="Site bg-greyLightest">
        <FixHistory />
        <ScrollRestoration />
        {Feature.isEnabled('dark_mode') && (
          <SimpleButton className="DMButton" icon="moon outline" onClick={this.toggleDarkMode} tertiary />
        )}
        <Header onToggleMenu={this.callToggleLeftMenu} />
        <div className="Site__container">
          {usr?.id ? (
            <>
              <UserMenu callbackToggleOpen={this.setToggleLeftMenu} />
              <div className="Site__container__content">{this.renderContent()}</div>
            </>
          ) : (
            this.renderContent()
          )}
        </div>
        <OnLineStatus />
        <NotificationsZone className="Site__notifications" />
      </div>
    )
  }
}

Site.propTypes = {
  addNotification: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    loaded: PropTypes.bool,
    loading: PropTypes.bool,
    usr: AuthUserSchema,
  }).isRequired,
  authAction: PropTypes.func.isRequired,
  authFetch: PropTypes.func.isRequired,
  authLoginByCredentials: PropTypes.func.isRequired,
  authLogout: PropTypes.func.isRequired,
  environmentOnlineSet: PropTypes.func.isRequired,
  environmentSet: PropTypes.func.isRequired,
  environmentVisibilitySet: PropTypes.func.isRequired,
  systemMessageCanceled: PropTypes.func.isRequired,
  systemMessageShow: PropTypes.func.isRequired,

  location: PropTypes.object,
}

Site.defaultProps = {
  location: { pathname: '/' },
}

export default connect(
  (state) => ({
    auth: state.auth,
    state,
  }),
  {
    addNotification: actions.notificationsAdd,
    authAction,
    authFetch,
    authLoginByCredentials,
    authLogout,
    environmentOnlineSet,
    environmentSet,
    environmentVisibilitySet,
    systemMessageCanceled,
    systemMessageShow,
  },
)(withMatch(Site))
